// Pure view-models for the installer screens (SCR-014..016, NAV-010 — REQ-044..049): AWS account
// links, the session list and console, credential intake and the change-approval gate. Nothing
// here touches the DOM, fetch or a timer; the *.tsx files call these functions with values they
// already hold (including `new Date()`) so every rule stays testable without a browser.
import type { ApiError } from "@nexus/ui";
import type { components } from "../../api/schema";
import { formatUsd, parseUsd } from "@nexus/ui";
import { lengthBetween, type FieldErrors, type Rule } from "@nexus/ui";
import type { ChipTone } from "@nexus/ui";

// --------------------------------------------------------------------------------- schema types
export type AwsCapability = components["schemas"]["AwsCapability"];
export type AwsAccountLink = components["schemas"]["AwsAccountLink"];
export type AwsAccountLinkCreated = components["schemas"]["AwsAccountLinkCreated"];
export type CreateAwsAccountLinkRequest = components["schemas"]["CreateAwsAccountLinkRequest"];
export type InstallerGoal = components["schemas"]["InstallerGoal"];
export type InstallerAccessMode = components["schemas"]["InstallerAccessMode"];
export type InstallerSessionState = components["schemas"]["InstallerSessionState"];
export type InstallerSession = components["schemas"]["InstallerSession"];
export type CreateInstallerSessionRequest = components["schemas"]["CreateInstallerSessionRequest"];
export type InstallerMessage = components["schemas"]["InstallerMessage"];
export type InstallerMessageKind = components["schemas"]["InstallerMessageKind"];
export type ToolOutcome = components["schemas"]["ToolOutcome"];
export type InstallerBudget = components["schemas"]["InstallerBudget"];
export type ExtendInstallerBudgetRequest = components["schemas"]["ExtendInstallerBudgetRequest"];
export type InstallerStreamEvent = components["schemas"]["InstallerStreamEvent"];
export type InstallerChange = components["schemas"]["InstallerChange"];
export type InstallerChangeKind = components["schemas"]["InstallerChangeKind"];
export type InstallerChangeStatus = components["schemas"]["InstallerChangeStatus"];
export type InstallerChangeResource = components["schemas"]["InstallerChangeResource"];
export type InstallerParameterChange = components["schemas"]["InstallerParameterChange"];
export type ApproveInstallerChangeRequest = components["schemas"]["ApproveInstallerChangeRequest"];
export type RejectInstallerChangeRequest = components["schemas"]["RejectInstallerChangeRequest"];
export type InstallerStsCredentials = components["schemas"]["InstallerStsCredentials"];
export type IdentityCenterStartRequest = components["schemas"]["IdentityCenterStartRequest"];
export type IdentityCenterStartResponse = components["schemas"]["IdentityCenterStartResponse"];
export type IdentityCenterPollResponse = components["schemas"]["IdentityCenterPollResponse"];
export type IdentityCenterSelectRequest = components["schemas"]["IdentityCenterSelectRequest"];
export type IdentityCenterStatus = components["schemas"]["IdentityCenterStatus"];

// -------------------------------------------------------------------------- NAV-010 route paths
export const INSTALLER_INDEX_PATH = "/installer";
export const INSTALLER_ACCOUNTS_PATH = "/installer/accounts/:orgId";
export const INSTALLER_SESSION_PATH = "/installer/sessions/:sessionId";
export const INSTALLER_CHANGE_PATH = "/installer/sessions/:sessionId/changes/:changeId";

export function installerAccountsPath(orgId: string): string {
  return `/installer/accounts/${encodeURIComponent(orgId)}`;
}
export function installerSessionPath(sessionId: string): string {
  return `/installer/sessions/${encodeURIComponent(sessionId)}`;
}
export function installerChangePath(sessionId: string, changeId: string): string {
  return `/installer/sessions/${encodeURIComponent(sessionId)}/changes/${encodeURIComponent(changeId)}`;
}

/** The "Linked AWS accounts" tab before a customer is chosen: `/installer?view=accounts` (the
 *  accounts screen itself is per customer, `/installer/accounts/:orgId`). */
export const INSTALLER_ACCOUNTS_VIEW = "accounts";
export const INSTALLER_ACCOUNTS_CHOOSER_PATH = `${INSTALLER_INDEX_PATH}?view=${INSTALLER_ACCOUNTS_VIEW}`;

export type InstallerTab = "installs" | "accounts";

export interface InstallerTabLink {
  id: InstallerTab;
  label: string;
  href: string;
}

/** The installer's two tabs; "Linked AWS accounts" goes straight to a customer's accounts when one is known. */
export function installerTabs(orgId: string | null): InstallerTabLink[] {
  return [
    { id: "installs", label: "Installs", href: INSTALLER_INDEX_PATH },
    { id: "accounts", label: "Linked AWS accounts", href: orgId ? installerAccountsPath(orgId) : INSTALLER_ACCOUNTS_CHOOSER_PATH },
  ];
}

/** Which tab `/installer?…` shows. */
export function installerIndexTab(query: URLSearchParams): InstallerTab {
  return query.get("view") === INSTALLER_ACCOUNTS_VIEW ? "accounts" : "installs";
}

// ------------------------------------------------------------ customer-sourced text safety
// Anything discovered in, or proposed for, a customer's AWS account (resource ids, parameter
// values, transcript bodies) is untrusted on the API side (app/untrusted, STATUS.md TASK-106) and
// is treated as data here too: every value below reaches the DOM as a text child (Preact escapes
// it; nothing here ever uses innerHTML or a markdown-to-HTML step), and this additionally rewrites
// control and bidi/invisible characters into a visible, literal escape so they cannot hide text or
// reorder what an approver reads on SCR-015/SCR-016.
const HIDDEN_OR_CONTROL =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F͏؜᠎​-‏‪-‮⁠-⁩ㅤ︀-️﻿ﾠ]/gu;

/** Renders control, bidi-override and other invisible characters as a literal `⟦U+XXXX⟧` escape. */
export function visibleText(input: string): string {
  // The regex only ever matches a character that exists, so codePointAt(0) is always defined here.
  return input.replace(HIDDEN_OR_CONTROL, (ch) => `⟦U+${ch.codePointAt(0)!.toString(16).toUpperCase().padStart(4, "0")}⟧`);
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

// ============================================================== SCR-014: AWS accounts
export const ALL_CAPABILITIES: readonly AwsCapability[] = ["discover", "deploy", "terraform", "cur"];

const CAPABILITY_LABELS: Record<AwsCapability, string> = {
  discover: "Discover",
  deploy: "Deploy",
  terraform: "Terraform",
  cur: "CUR reads",
};

export function capabilityLabel(capability: AwsCapability): string {
  return CAPABILITY_LABELS[capability];
}

export function toggleCapability(current: readonly AwsCapability[], capability: AwsCapability): AwsCapability[] {
  return current.includes(capability) ? current.filter((c) => c !== capability) : [...current, capability];
}

const AWS_ACCOUNT_ID_PATTERN = /^[0-9]{12}$/;

export const awsAccountIdRule: Rule = (value) => (AWS_ACCOUNT_ID_PATTERN.test(value.trim()) ? null : "A 12-digit AWS account id.");

export interface LinkAccountDraft {
  awsAccountId: string;
  capabilities: AwsCapability[];
}

export const emptyLinkAccountDraft: LinkAccountDraft = { awsAccountId: "", capabilities: [] };

export function validateLinkAccount(draft: LinkAccountDraft): FieldErrors {
  const errors: FieldErrors = {};
  const problem = awsAccountIdRule(draft.awsAccountId);
  if (problem) errors.awsAccountId = problem;
  if (draft.capabilities.length === 0) errors.capabilities = "Choose at least one capability.";
  return errors;
}

export function linkAccountRequest(draft: LinkAccountDraft): CreateAwsAccountLinkRequest {
  return { awsAccountId: draft.awsAccountId.trim(), capabilities: [...draft.capabilities] };
}

export interface AccountRow {
  id: string;
  awsAccountId: string;
  capabilities: AwsCapability[];
  roleArn: string | null;
  templateVersion: string | null;
  verifiedAt: string | null;
  verified: boolean;
}

export function accountRow(link: AwsAccountLink): AccountRow {
  return {
    id: link.id,
    awsAccountId: link.awsAccountId,
    capabilities: link.capabilities,
    roleArn: link.roleArn,
    templateVersion: link.templateVersion,
    verifiedAt: link.verifiedAt,
    verified: link.verifiedAt !== null,
  };
}

export interface LinkCreatedView {
  id: string;
  awsAccountId: string;
  quickCreateUrl: string;
  externalId: string;
  instructions: string;
  /** API-039: the 201 create response is the only time the wire ever carries `externalId` —
   *  every later read of this link blanks it. */
  warning: string;
}

/** The result card SCR-014 shows once, right after linking: the only time `externalId` is returned. */
export function linkCreatedView(created: AwsAccountLinkCreated): LinkCreatedView {
  return {
    id: created.id,
    awsAccountId: created.awsAccountId,
    quickCreateUrl: created.quickCreateUrl,
    externalId: created.externalId,
    instructions:
      "Open the quick-create link in the customer's AWS account, keep the pre-filled ExternalId, and create the stack. " +
      "Come back here and press Verify once the stack shows CREATE_COMPLETE.",
    warning: "Copy the ExternalId now — it will not be shown again. If it is lost, delete this link and create a new one.",
  };
}

const ASSUME_ROLE_REASON_LABELS: Record<string, string> = {
  access_denied: "AWS denied the assume-role call.",
  external_id_mismatch: "The stack's ExternalId does not match this link's.",
  role_missing: "The bootstrap role does not exist in that account yet.",
};

/** The content-free reason of a 424 `assume_role_failed`, from the error's field-less detail. */
export function assumeRoleFailureReason(error: ApiError): string | null {
  const detail = error.details.find((d) => d.field === undefined || d.field === "");
  return detail ? detail.code : null;
}

export function assumeRoleFailureMessage(error: ApiError): string {
  const reason = assumeRoleFailureReason(error);
  if (reason === null) return error.message;
  return ASSUME_ROLE_REASON_LABELS[reason] ?? `The role could not be assumed (${reason}).`;
}

const BOOTSTRAP_STACK_REASON_LABELS: Record<string, string> = {
  stack_missing: "The bootstrap CloudFormation stack was not found in that account.",
  access_denied: "The assumed role could not read the bootstrap stack.",
  outputs_invalid: "The stack's outputs do not describe this link (a stale template, or the wrong stack).",
};

/**
 * `verifyAwsAccountLink`'s two 424s share one field-less detail shape (a content-free reason) but
 * different meanings — `assume_role_failed` never got past assuming the role; `bootstrap_stack_invalid`
 * assumed it but could not read (or trust) the stack. Dispatches on the error's own code so SCR-014
 * always shows the right one of the two reason tables.
 */
export function verifyFailureMessage(error: ApiError): string {
  const reason = assumeRoleFailureReason(error);
  if (error.code === "bootstrap_stack_invalid") {
    if (reason === null) return error.message;
    return BOOTSTRAP_STACK_REASON_LABELS[reason] ?? `The bootstrap stack could not be verified (${reason}).`;
  }
  return assumeRoleFailureMessage(error);
}

// ============================================================== installer off
/** The message every installer route answers (503 `unavailable`) when the deployment runs no
 *  installer (NEXUS_INSTALLER_ENABLED=false): the API's one unavailable error with a known cause. */
export const INSTALLER_DISABLED_MESSAGE = "the installer is not enabled in this deployment";

/** Whether error is the deployment's installer being off, rather than an outage worth retrying. */
export function isInstallerDisabled(error: ApiError | null | undefined): boolean {
  return !!error && error.code === "unavailable" && error.message === INSTALLER_DISABLED_MESSAGE;
}

// ============================================================== session list
const SESSION_STATE_LABELS: Record<InstallerSessionState, string> = {
  created: "Creating",
  awaiting_credentials: "Awaiting credentials",
  discovering: "Discovering",
  planning: "Planning",
  awaiting_approval: "Awaiting approval",
  applying: "Applying",
  verifying: "Verifying",
  done: "Done",
  paused: "Paused (budget)",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function sessionStateLabel(state: InstallerSessionState): string {
  return SESSION_STATE_LABELS[state];
}

/** Every session state, in the order a session moves through them (the Status filter's choices). */
export const SESSION_STATES: readonly InstallerSessionState[] = [
  "created",
  "awaiting_credentials",
  "discovering",
  "planning",
  "awaiting_approval",
  "applying",
  "verifying",
  "done",
  "paused",
  "failed",
  "cancelled",
];

const SESSION_STATE_TONES: Record<InstallerSessionState, ChipTone> = {
  created: "blue",
  awaiting_credentials: "amber",
  discovering: "blue",
  planning: "blue",
  awaiting_approval: "amber",
  applying: "blue",
  verifying: "blue",
  done: "green",
  paused: "amber",
  failed: "red",
  cancelled: "grey",
};

/** The status chip's colour: amber when a person must act, green done, red failed, grey cancelled, blue at work. */
export function sessionStateTone(state: InstallerSessionState): ChipTone {
  return SESSION_STATE_TONES[state];
}

export const GOALS: readonly InstallerGoal[] = ["install", "upgrade"];
export const ACCESS_MODES: readonly InstallerAccessMode[] = ["sts_keys", "identity_center", "bootstrap_role"];

const GOAL_LABELS: Record<InstallerGoal, string> = { install: "Install", upgrade: "Upgrade" };
export function goalLabel(goal: InstallerGoal): string {
  return GOAL_LABELS[goal];
}

const ACCESS_MODE_LABELS: Record<InstallerAccessMode, string> = {
  sts_keys: "Pasted STS keys",
  identity_center: "IAM Identity Center",
  bootstrap_role: "Bootstrap role",
};
export function accessModeLabel(mode: InstallerAccessMode): string {
  return ACCESS_MODE_LABELS[mode];
}

const ACCESS_MODE_HINTS: Record<InstallerAccessMode, string> = {
  sts_keys: "You paste temporary (ASIA…) keys for the account once the session starts. They are held in memory for at most an hour.",
  identity_center: "You sign in to the customer's IAM Identity Center with a device code, then pick the account and permission set.",
  bootstrap_role: "No credentials: the installer assumes the linked account's bootstrap role. Needs a verified link.",
};
/** One line under the Access mode field saying what the chosen mode asks of the operator. */
export function accessModeHint(mode: InstallerAccessMode): string {
  return ACCESS_MODE_HINTS[mode];
}

const TERMINAL_SESSION_STATES = new Set<InstallerSessionState>(["done", "failed", "cancelled"]);
export function isSessionTerminal(state: InstallerSessionState): boolean {
  return TERMINAL_SESSION_STATES.has(state);
}

export interface SessionRow {
  id: string;
  orgId: string;
  awsAccountId: string;
  goalLabel: string;
  accessModeLabel: string;
  state: InstallerSessionState;
  stateLabel: string;
  stateTone: ChipTone;
  pendingChanges: number;
  createdAt: string;
  href: string;
}

export function sessionRow(session: InstallerSession): SessionRow {
  return {
    id: session.id,
    orgId: session.orgId,
    awsAccountId: session.awsAccountId,
    goalLabel: goalLabel(session.goal),
    accessModeLabel: accessModeLabel(session.accessMode),
    state: session.state,
    stateLabel: sessionStateLabel(session.state),
    stateTone: sessionStateTone(session.state),
    pendingChanges: session.pendingChanges,
    createdAt: session.createdAt,
    href: installerSessionPath(session.id),
  };
}

export interface SessionListFilter {
  orgId: string;
  state: InstallerSessionState | "";
}

export const emptySessionListFilter: SessionListFilter = { orgId: "", state: "" };

/** Whether the list should show the first-run empty state: nothing filtered, nothing loading, no sessions. */
export function isFirstRun(filter: SessionListFilter, list: { loading: boolean; error: unknown; rows: readonly unknown[] }): boolean {
  return !list.loading && !list.error && list.rows.length === 0 && filter.orgId.trim() === "" && filter.state === "";
}

/** The account ids offered under the new-session form's AWS account field: the customer's linked
 *  accounts, verified ones first (only a verified link can run a session). */
export function linkedAccountChoices(rows: readonly AccountRow[]): { awsAccountId: string; verified: boolean }[] {
  const seen = new Set<string>();
  const choices: { awsAccountId: string; verified: boolean }[] = [];
  for (const row of [...rows].sort((a, b) => Number(b.verified) - Number(a.verified))) {
    if (seen.has(row.awsAccountId)) continue;
    seen.add(row.awsAccountId);
    choices.push({ awsAccountId: row.awsAccountId, verified: row.verified });
  }
  return choices;
}

/** Query parameters `listInstallerSessions` accepts, omitting an unset filter rather than sending it empty. */
export function sessionListQuery(filter: SessionListFilter): { orgId?: string; state?: InstallerSessionState } {
  const query: { orgId?: string; state?: InstallerSessionState } = {};
  const orgId = filter.orgId.trim();
  if (orgId !== "") query.orgId = orgId;
  if (filter.state !== "") query.state = filter.state;
  return query;
}

const ORG_ID_PATTERN = /^[a-z0-9-]{1,64}$/;
const AWS_REGION_PATTERN = /^[a-z]{2}(-[a-z]+)+-[0-9]+$/;

export interface CreateSessionDraft {
  orgId: string;
  awsAccountId: string;
  regions: string;
  goal: InstallerGoal;
  accessMode: InstallerAccessMode;
  installationId: string;
}

export const emptyCreateSessionDraft: CreateSessionDraft = {
  orgId: "",
  awsAccountId: "",
  regions: "",
  goal: "install",
  accessMode: "sts_keys",
  installationId: "",
};

/** Splits the composer's free-text region list on commas, spaces or newlines, deduplicated. */
export function parseRegions(input: string): string[] {
  const seen = new Set<string>();
  for (const part of input.split(/[\s,]+/)) {
    const region = part.trim();
    if (region !== "") seen.add(region);
  }
  return [...seen];
}

export function validateCreateSession(draft: CreateSessionDraft): FieldErrors {
  const errors: FieldErrors = {};
  const orgId = draft.orgId.trim();
  if (orgId === "") errors.orgId = "Choose a customer.";
  else if (!ORG_ID_PATTERN.test(orgId)) errors.orgId = "Lowercase letters, digits and hyphens, 1-64 characters.";
  const accountProblem = awsAccountIdRule(draft.awsAccountId);
  if (accountProblem) errors.awsAccountId = accountProblem;
  const regions = parseRegions(draft.regions);
  if (regions.length === 0 || regions.length > 20) errors.regions = "List 1-20 AWS regions.";
  else if (regions.some((r) => !AWS_REGION_PATTERN.test(r))) errors.regions = "Use AWS region codes, e.g. us-east-1.";
  if (draft.goal === "upgrade" && draft.installationId.trim() === "") errors.installationId = "Required for an upgrade.";
  return errors;
}

export function createSessionRequest(draft: CreateSessionDraft): CreateInstallerSessionRequest {
  const body: CreateInstallerSessionRequest = {
    orgId: draft.orgId.trim(),
    awsAccountId: draft.awsAccountId.trim(),
    regions: parseRegions(draft.regions),
    goal: draft.goal,
    accessMode: draft.accessMode,
  };
  if (draft.goal === "upgrade") body.installationId = draft.installationId.trim();
  return body;
}

// ============================================================== SCR-015: session console
// Callers only reach here once they have already special-cased ms <= 0 ("Time's up"/"Expired").
function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

interface BudgetFields {
  budgetMicroUsd: number;
  spentMicroUsd: number;
  turns: number;
  maxTurns: number;
  deadlineAt: string;
}

export interface BudgetMeterView {
  spentText: string;
  limitText: string;
  /** 0-100, clamped; a zero-limit budget already spent shows full. */
  percent: number;
  turnsText: string;
  timeLeftText: string;
  exhausted: boolean;
}

/** The header's budget meter (SCR-015): works from either a session or a live `budget` stream event. */
export function budgetMeterView(budget: BudgetFields, now: Date): BudgetMeterView {
  const { budgetMicroUsd, spentMicroUsd, turns, maxTurns, deadlineAt } = budget;
  const percent = budgetMicroUsd <= 0 ? (spentMicroUsd > 0 ? 100 : 0) : Math.min(100, Math.round((spentMicroUsd / budgetMicroUsd) * 100));
  const msLeft = Date.parse(deadlineAt) - now.getTime();
  return {
    spentText: formatUsd(spentMicroUsd),
    limitText: formatUsd(budgetMicroUsd),
    percent,
    turnsText: `${turns} / ${maxTurns}`,
    timeLeftText: msLeft <= 0 ? "Time's up" : formatDuration(msLeft),
    exhausted: spentMicroUsd >= budgetMicroUsd || turns >= maxTurns || msLeft <= 0,
  };
}

export interface SessionHeaderView {
  orgId: string;
  awsAccountId: string;
  regions: string;
  goalLabel: string;
  accessModeLabel: string;
  stateLabel: string;
  budget: BudgetMeterView;
  canCancel: boolean;
  canExtend: boolean;
  canCompose: boolean;
}

/** The console header (SCR-015): who/what/where, the budget meter and which controls apply. */
export function sessionHeaderView(session: InstallerSession, now: Date, canApprove: boolean): SessionHeaderView {
  const terminal = isSessionTerminal(session.state);
  return {
    orgId: session.orgId,
    awsAccountId: session.awsAccountId,
    regions: session.regions.join(", "),
    goalLabel: goalLabel(session.goal),
    accessModeLabel: accessModeLabel(session.accessMode),
    stateLabel: sessionStateLabel(session.state),
    budget: budgetMeterView(session, now),
    canCancel: !terminal,
    canExtend: canApprove && !terminal,
    canCompose: !terminal && session.state !== "awaiting_credentials",
  };
}

// -------------------------------------------------------------- extend budget (installer:approve)
export interface ExtendBudgetDraft {
  addUsd: string;
  addTurns: string;
  addMinutes: string;
}

export const emptyExtendBudgetDraft: ExtendBudgetDraft = { addUsd: "", addTurns: "", addMinutes: "" };

const MAX_BUDGET_EXTENSION_USD = 1_000; // domain.MaxBudgetExtension: $1,000
const MAX_TURNS_EXTENSION = 1_000;
const MAX_MINUTES_EXTENSION = 1_440; // 24h

function intOrZero(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return 0;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

export function validateExtendBudget(draft: ExtendBudgetDraft): FieldErrors {
  const errors: FieldErrors = {};
  const usdMicro = draft.addUsd.trim() === "" ? 0n : parseUsd(draft.addUsd);
  if (usdMicro === null || usdMicro < 0n) errors.addUsd = "A dollar amount.";
  else if (usdMicro > BigInt(MAX_BUDGET_EXTENSION_USD) * 1_000_000n) errors.addUsd = `At most $${MAX_BUDGET_EXTENSION_USD}.`;
  const turns = intOrZero(draft.addTurns);
  if (turns === null || turns < 0 || turns > MAX_TURNS_EXTENSION) errors.addTurns = `A whole number 0-${MAX_TURNS_EXTENSION}.`;
  const minutes = intOrZero(draft.addMinutes);
  if (minutes === null || minutes < 0 || minutes > MAX_MINUTES_EXTENSION) errors.addMinutes = `A whole number 0-${MAX_MINUTES_EXTENSION}.`;
  // Reaching here with no field error already means usdMicro/turns/minutes are non-null.
  if (Object.keys(errors).length === 0 && usdMicro === 0n && turns === 0 && minutes === 0) {
    errors.addUsd = "An extension must add something.";
  }
  return errors;
}

export function extendBudgetRequest(draft: ExtendBudgetDraft): ExtendInstallerBudgetRequest {
  const usdMicro = draft.addUsd.trim() === "" ? 0n : (parseUsd(draft.addUsd) ?? 0n);
  return {
    addMicroUsd: Number(usdMicro),
    addTurns: intOrZero(draft.addTurns) ?? 0,
    addMinutes: intOrZero(draft.addMinutes) ?? 0,
  };
}

// -------------------------------------------------------------- transcript rows
const MESSAGE_ACTOR_LABELS: Record<InstallerMessageKind, string> = {
  operator: "Operator",
  assistant: "Agent",
  tool_call: "Tool call",
  tool_result: "Tool result",
  system_event: "System",
};

const TOOL_OUTCOME_LABELS: Record<ToolOutcome, string> = { ok: "ok", rejected: "rejected", error: "error" };

export interface TranscriptToolView {
  name: string;
  durationText: string;
  outcomeLabel: string | null;
  detail: string;
}

export interface TranscriptRowView {
  seq: number;
  kind: InstallerMessageKind;
  actorLabel: string;
  /** Plain text for operator/assistant/system_event rows; null for a collapsed tool row. */
  text: string | null;
  tool: TranscriptToolView | null;
  createdAt: string;
}

function messageText(body: Record<string, unknown>): string {
  const text = body["text"];
  return typeof text === "string" ? text : safeJson(body);
}

/** One transcript row (SCR-015 left panel): text rows plain, tool calls collapsed by name/duration/outcome. */
export function transcriptRow(message: InstallerMessage): TranscriptRowView {
  const isTool = message.kind === "tool_call" || message.kind === "tool_result";
  return {
    seq: message.seq,
    kind: message.kind,
    actorLabel: MESSAGE_ACTOR_LABELS[message.kind],
    text: isTool ? null : visibleText(messageText(message.body)),
    tool: isTool
      ? {
          name: message.toolName ?? "(unnamed tool)",
          durationText: message.durationMs === null ? "—" : `${message.durationMs} ms`,
          outcomeLabel: message.outcome === null ? null : TOOL_OUTCOME_LABELS[message.outcome],
          detail: visibleText(safeJson(message.body)),
        }
      : null,
    createdAt: message.createdAt,
  };
}

// -------------------------------------------------------------- the SSE reducer
export interface ConsoleState {
  session: InstallerSession | null;
  budget: InstallerBudget | null;
  messages: InstallerMessage[];
  changes: InstallerChange[];
}

export const emptyConsoleState: ConsoleState = { session: null, budget: null, messages: [], changes: [] };

/**
 * Folds one `InstallerStreamEvent` into the console's state (API-040 `streamInstallerEvents`).
 * Pure and idempotent: a `message` already seen by `seq` is dropped (a reconnect resends the
 * snapshot plus every row after `Last-Event-ID`, which can overlap what is already held), a
 * `change` replaces its id, and `state`/`budget` replace outright — exactly the resume semantics
 * the API documents.
 */
export function applyStreamEvent(state: ConsoleState, event: InstallerStreamEvent): ConsoleState {
  switch (event.type) {
    case "state":
      return event.session ? { ...state, session: event.session } : state;
    case "budget":
      return event.budget ? { ...state, budget: event.budget } : state;
    case "change": {
      const change = event.change;
      if (!change) return state;
      return { ...state, changes: [...state.changes.filter((c) => c.id !== change.id), change] };
    }
    case "message": {
      const message = event.message;
      if (!message) return state;
      if (state.messages.some((m) => m.seq === message.seq)) return state;
      return { ...state, messages: [...state.messages, message].sort((a, b) => a.seq - b.seq) };
    }
    default:
      return state;
  }
}

export function pendingChanges(changes: readonly InstallerChange[]): InstallerChange[] {
  return changes.filter((c) => c.status === "proposed");
}

/**
 * Whether a `streamInstallerEvents` event means the SSE connection should now close (console.tsx):
 * a session that has reached a terminal state (`done`, `failed`, `cancelled`) will send no further
 * events, so leaving the connection open only buys endless reconnects — each one billed as session
 * activity by the server that streams it. Only a `state` event can carry this; every other event
 * type (`message`, `change`, `budget`) leaves the stream open.
 */
export function streamEventEndsSession(event: InstallerStreamEvent): boolean {
  return event.type === "state" && event.session !== undefined && isSessionTerminal(event.session.state);
}

// -------------------------------------------------------------- the proposal card
export const PROPOSE_INSTALL_CONFIG_TOOL = "propose_install_config";

export interface ProposalField {
  label: string;
  value: string;
  /** The help topic behind the label's "?", for a field whose choice needs explaining. */
  help?: string;
}

export interface ProposalView {
  /** The transcript seq of the tool call this reads, so a screen can key/scroll to it. */
  seq: number;
  fields: ProposalField[];
}

const PROPOSAL_FIELDS: readonly [key: string, label: string][] = [
  ["goal", "Goal"],
  ["region", "Region"],
  ["iacFlavor", "IaC flavour"],
  ["name", "Name"],
  ["vpcMode", "VPC mode"],
  ["vpcId", "VPC"],
  ["subnetIds", "Subnets"],
  ["models", "Models"],
  ["authModes", "Auth modes"],
  ["promptLogging", "Prompt logging"],
  ["albScheme", "ALB scheme"],
  ["certificateArn", "Certificate"],
  ["sizing", "Sizing"],
  ["useVpcEndpoints", "VPC endpoints"],
  ["bedrockRoleArn", "Bedrock role"],
  ["imageUri", "Image"],
  ["alarmTopicArn", "Alarm topic"],
];

function stringifyProposalValue(value: unknown): string {
  if (value === null || value === undefined) return "(not set)";
  if (Array.isArray(value)) return value.length === 0 ? "(none)" : value.map(String).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return safeJson(value);
  return String(value);
}

/**
 * The typed configuration card (SCR-015): built from the transcript's latest
 * `propose_install_config` tool call, not a dedicated endpoint (there is none — the proposal lives
 * only in the session's transcript, ERD §3.7). Every value is rendered as data (`visibleText`):
 * discovered resource ids and names came out of the customer's account.
 */
export function latestProposal(messages: readonly InstallerMessage[]): ProposalView | null {
  const calls = messages.filter((m) => m.kind === "tool_call" && m.toolName === PROPOSE_INSTALL_CONFIG_TOOL);
  const last = calls[calls.length - 1];
  if (!last) return null;
  const input = last.body;
  const fields: ProposalField[] = [];
  for (const [key, label] of PROPOSAL_FIELDS) {
    if (!(key in input)) continue;
    fields.push({ label, value: visibleText(stringifyProposalValue(input[key])) });
  }
  fields.push(billingIdentityField(input));
  return { seq: last.seq, fields };
}

const BILLING_IDENTITY_LABELS: Record<string, string> = {
  email: "Email (each person's email in the customer's AWS billing data)",
  pseudonymous: "Pseudonymous (a p- id, resolvable only on the proxy's People screen)",
};

/**
 * The billing identity row (ADR-109), always shown because it decides what the payer account's
 * billing data reveals: an install's choice, `email` when the agent named none (the server's
 * default); an upgrade keeps the installation's own, whatever the proposal says.
 */
function billingIdentityField(input: Record<string, unknown>): ProposalField {
  const help = "installer.billingIdentity";
  const label = "Billing identity";
  if (input["goal"] === "upgrade") return { label, help, value: "Kept from the installation" };
  const raw = input["bedrockAttributionIdentity"] ?? "email";
  const value = typeof raw === "string" ? BILLING_IDENTITY_LABELS[raw] : undefined;
  return { label, help, value: value ?? visibleText(stringifyProposalValue(raw)) };
}

// -------------------------------------------------------------- verification checklist
export interface VerificationStep {
  key: string;
  label: string;
  done: boolean;
}

const VERIFICATION_ORDER: InstallerSessionState[] = [
  "discovering",
  "planning",
  "awaiting_approval",
  "applying",
  "verifying",
  "done",
];

/** SCR-015's verification checklist: each step is done once the session has passed it. */
export function verificationChecklist(state: InstallerSessionState): VerificationStep[] {
  const index = VERIFICATION_ORDER.indexOf(state);
  const passed = state === "done" ? VERIFICATION_ORDER.length : index;
  return [
    { key: "discover", label: "Discover the account", done: passed > VERIFICATION_ORDER.indexOf("discovering") },
    { key: "plan", label: "Propose a configuration", done: passed > VERIFICATION_ORDER.indexOf("planning") },
    { key: "apply", label: "Apply the approved changes", done: passed > VERIFICATION_ORDER.indexOf("applying") },
    { key: "health", label: "Verify health and a test request", done: passed > VERIFICATION_ORDER.indexOf("verifying") },
    { key: "done", label: "Register the installation", done: state === "done" },
  ];
}

// ============================================================== credentials panel (SCR-015)
export interface CredentialsDraft {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
}

/** The panel's state at rest and right after a submit: no field ever holds a pasted value. */
export const emptyCredentialsDraft: CredentialsDraft = { accessKeyId: "", secretAccessKey: "", sessionToken: "" };

export type CredentialsFieldErrors = Partial<Record<keyof CredentialsDraft, string>>;

const STS_ACCESS_KEY_PATTERN = /^[A-Z0-9]{16,128}$/;

/** The domain's own refusal text (`domain.ErrLongTermKey`, API-041 `long_term_keys_refused`), so
 * the client-side refusal reads exactly like the server's would. */
export const LONG_TERM_KEY_MESSAGE = "long-term keys are refused";

export function validateCredentialsDraft(draft: CredentialsDraft): CredentialsFieldErrors {
  const errors: CredentialsFieldErrors = {};
  const key = draft.accessKeyId.trim();
  if (key === "") errors.accessKeyId = "Required.";
  else if (!STS_ACCESS_KEY_PATTERN.test(key)) errors.accessKeyId = "Not an access key id.";
  else if (key.startsWith("AKIA")) errors.accessKeyId = LONG_TERM_KEY_MESSAGE;
  else if (!key.startsWith("ASIA")) errors.accessKeyId = "Must be a temporary (ASIA) key.";
  if (draft.secretAccessKey === "") errors.secretAccessKey = "Required.";
  if (draft.sessionToken === "") errors.sessionToken = "Required.";
  return errors;
}

export function credentialsAreValid(draft: CredentialsDraft): boolean {
  return Object.keys(validateCredentialsDraft(draft)).length === 0;
}

/** The exact wire body — trimmed only where IAM ids allow it (never inside the secret or token). */
export function credentialsRequestBody(draft: CredentialsDraft): InstallerStsCredentials {
  return { accessKeyId: draft.accessKeyId.trim(), secretAccessKey: draft.secretAccessKey, sessionToken: draft.sessionToken };
}

/**
 * What the panel's state becomes the instant a submit is under way: emptied before the request
 * settles, so the credentials are sent once and never held in state beyond that submit (REQ-044,
 * TASK-122's security requirement). Call this to build the request body's inputs first, then call
 * it again (or just use its result) to reset the draft — never keep the original object around.
 */
export function clearCredentialsDraft(): CredentialsDraft {
  return { accessKeyId: "", secretAccessKey: "", sessionToken: "" };
}

// -------------------------------------------------------------- Identity Center
export interface IdentityCenterStartDraft {
  startUrl: string;
  region: string;
}

export const emptyIdentityCenterStart: IdentityCenterStartDraft = { startUrl: "", region: "" };

export function validateIdentityCenterStart(draft: IdentityCenterStartDraft): FieldErrors {
  const errors: FieldErrors = {};
  if (!/^https:\/\//.test(draft.startUrl.trim())) errors.startUrl = "An https:// start URL.";
  if (!AWS_REGION_PATTERN.test(draft.region.trim())) errors.region = "An AWS region code.";
  return errors;
}

export function identityCenterStartRequest(draft: IdentityCenterStartDraft): IdentityCenterStartRequest {
  return { startUrl: draft.startUrl.trim(), region: draft.region.trim() };
}

export function identityCenterSelectRequest(accountId: string, roleName: string): IdentityCenterSelectRequest {
  return { accountId, roleName };
}

// ============================================================== SCR-016: change approval
const CHANGE_STATUS_LABELS: Record<InstallerChangeStatus, string> = {
  proposed: "Proposed",
  approved: "Approved",
  applying: "Applying",
  applied: "Applied",
  failed: "Failed",
  rejected: "Rejected",
  expired: "Expired",
};
export function changeStatusLabel(status: InstallerChangeStatus): string {
  return CHANGE_STATUS_LABELS[status];
}

const CHANGE_STATUS_TONES: Record<InstallerChangeStatus, ChipTone> = {
  proposed: "amber",
  approved: "blue",
  applying: "blue",
  applied: "green",
  failed: "red",
  rejected: "grey",
  expired: "grey",
};
/** A change's chip colour: amber while it waits for a person, green applied, red failed. */
export function changeStatusTone(status: InstallerChangeStatus): ChipTone {
  return CHANGE_STATUS_TONES[status];
}

const CHANGE_KIND_LABELS: Record<InstallerChangeKind, string> = {
  nexus_registration: "Nexus registration",
  cfn_change_set: "CloudFormation change set",
  tf_plan: "Terraform plan",
};
export function changeKindLabel(kind: InstallerChangeKind): string {
  return CHANGE_KIND_LABELS[kind];
}

const RISK_FLAG_LABELS: Record<string, string> = {
  public_alb: "Internet-facing load balancer",
  open_cidr: "Security group open to 0.0.0.0/0",
  prompt_logging_on: "Prompt logging is on",
  iam_change: "Creates or changes an IAM role",
  deletion: "Deletes a resource",
  replacement: "Replaces a resource (it is recreated)",
  image_not_pinned: "Image is not the release's pinned digest",
  bedrock_role_arn: "Assumes a Bedrock role",
};

/** A risk flag's label; the enum is open (API-042), so an unlisted flag still reads as words. */
export function riskFlagLabel(flag: string): string {
  return RISK_FLAG_LABELS[flag] ?? visibleText(flag.replace(/_/g, " "));
}

/** Resource rows with every customer-sourced string made safe to display (see `visibleText`). */
export function sanitizedResources(resources: readonly InstallerChangeResource[]): InstallerChangeResource[] {
  return resources.map((r) => ({ ...r, type: visibleText(r.type), logicalId: visibleText(r.logicalId) }));
}

/** Parameter rows with every customer-sourced string made safe to display (see `visibleText`). */
export function sanitizedParameters(parameters: readonly InstallerParameterChange[]): InstallerParameterChange[] {
  return parameters.map((p) => ({
    name: visibleText(p.name),
    from: p.from === null ? null : visibleText(p.from),
    to: p.to === null ? null : visibleText(p.to),
  }));
}

function expiresInText(expiresAt: string, now: Date): string {
  const ms = Date.parse(expiresAt) - now.getTime();
  return ms <= 0 ? "Expired" : `Expires in ${formatDuration(ms)}`;
}

export interface ChangeApprovalView {
  statusLabel: string;
  kindLabel: string;
  digest: string;
  expiresInText: string;
  /** Past its 24 h window and still (nominally) proposed — REQ-046; the server would answer `change_expired`. */
  isExpired: boolean;
  riskFlags: string[];
  /** `installer:approve`, the change is proposed, and it is not expired. */
  canReview: boolean;
  /** `canReview` and the "I reviewed the diff above" tick is checked — the two-step gate. */
  canApprove: boolean;
  canReject: boolean;
}

/**
 * SCR-016's view: risk flags first, the digest exactly as the server holds it (never edited —
 * `approveChangeRequest` echoes this same value), and approve disabled whenever the change is not
 * a live proposal the caller may act on, or the reviewer has not ticked the box yet.
 */
export function changeApprovalView(change: InstallerChange, now: Date, hasApprovePermission: boolean, reviewed: boolean): ChangeApprovalView {
  const isExpired = change.status === "proposed" && Date.parse(change.expiresAt) <= now.getTime();
  const canReview = hasApprovePermission && change.status === "proposed" && !isExpired;
  return {
    statusLabel: changeStatusLabel(change.status),
    kindLabel: changeKindLabel(change.kind),
    digest: change.artifactDigest,
    expiresInText: expiresInText(change.expiresAt, now),
    isExpired,
    riskFlags: change.summary.riskFlags.map(riskFlagLabel),
    canReview,
    canApprove: canReview && reviewed,
    canReject: hasApprovePermission && change.status === "proposed",
  };
}

/** Approve always binds to the change's own current digest — never a value the UI could edit. */
export function approveChangeRequest(change: InstallerChange): ApproveInstallerChangeRequest {
  return { artifactDigest: change.artifactDigest };
}

export const rejectReasonRule: Rule = lengthBetween(1, 500);

export function rejectChangeRequest(reason: string): RejectInstallerChangeRequest {
  return { reason: reason.trim() };
}

const APPROVAL_ERROR_MESSAGES: Record<string, string> = {
  artifact_changed: "The proposal changed since this was loaded. Reload to review the new diff before approving.",
  change_expired: "The approval window has expired. Ask the agent to re-plan.",
  change_not_proposed: "This change is no longer awaiting approval.",
  credentials_required: "The session needs credentials again before this change can apply.",
};

/** A plain-language gloss of a refused approve/reject, over the server's own message where none fits. */
export function approvalErrorMessage(error: ApiError): string {
  return APPROVAL_ERROR_MESSAGES[error.code] ?? error.message;
}

const SESSION_ACTION_ERROR_MESSAGES: Record<string, string> = {
  session_on_other_worker: "Another admin task is currently handling this session. Try again in a moment.",
  session_terminal: "This session has already ended.",
};

/** A plain-language gloss of a refused message/cancel/extend, over the server's own message. */
export function sessionActionErrorMessage(error: ApiError): string {
  return SESSION_ACTION_ERROR_MESSAGES[error.code] ?? error.message;
}

const CREATE_SESSION_ERROR_MESSAGES: Record<string, string> = {
  session_active: "This org already has a live installer session for this account. Open it instead of starting another.",
  link_not_verified: "This AWS account's bootstrap link is not verified yet. Verify it on the org's AWS accounts page first.",
  installer_daily_budget_exhausted: "Today's fleet-wide installer model budget is spent. Try again after midnight UTC.",
};

/** A plain-language gloss of a refused `POST /v1/installer/sessions`, over the server's own message. */
export function createSessionErrorMessage(error: ApiError): string {
  return CREATE_SESSION_ERROR_MESSAGES[error.code] ?? error.message;
}
